import ApiClient from 'api-client/ApiClient'
import { listTenants } from 'app/plugins/account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'app/plugins/account/components/userManagement/tenants/selectors'
import ListTable from 'core/components/listTable/ListTable'
import Progress from 'core/components/progress/Progress'
import Text from 'core/elements/Text'
import ModalForm from 'core/elements/modal/ModalForm'
import useDeleteAction from 'core/hooks/useDeleteAction'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { deleteNetworkRBACPolicy, listNetworks } from './actions'

const columns = [
  { id: 'id', label: 'UUID', display: false },
  { id: 'name', label: 'Tenant' },
]

const { neutron } = ApiClient.getInstance()

const ShareNetworkWithSpecificTenants = ({ onClose, network }) => {
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const { params, updateParams } = useParams({ selectedTenants: [] })

  // Filter out the tenant that owns the network
  const { reload: reloadNetworks } = useListAction(listNetworks)
  const { loading: loadingTenants } = useListAction(listTenants)
  const tenants = useSelector(tenantsSelector)
  const filteredTenants = tenants.filter((tenant) => tenant.id !== network?.project_id)

  // Action to remove the policies in bulk
  const { bulkRemove } = useDeleteAction(deleteNetworkRBACPolicy)

  // List of existing tenant IDs
  const existingTenantIds = useMemo(
    () => network?.sharedTenants?.map((sharedTenant) => sharedTenant?.target_tenant) || [],
    [network?.sharedTenants],
  )

  // Update selected tenants
  useEffect(() => {
    if (tenants.length > 0 && existingTenantIds?.length > 0) {
      updateParams({
        selectedTenants: tenants.filter((tenant) => existingTenantIds?.includes(tenant.id)),
      })
    }
  }, [existingTenantIds, tenants])

  // Submit form
  const submitForm = useCallback(async () => {
    try {
      setSubmitting(true)
      const selectedTenantIds = params.selectedTenants.flatMap((tenant) => tenant.id)

      // For tenants that are no longer selected, delete the RBAC policy
      if (existingTenantIds?.length > 0) {
        // Skip tenants that are still selected
        const itemsToRemove = network?.sharedTenants
          ?.filter((sharedTenant) => {
            return !selectedTenantIds.includes(sharedTenant?.target_tenant)
          })
          ?.map((sharedTenant) => ({ policyId: sharedTenant?.id }))
        await bulkRemove(itemsToRemove)
      }

      // For tenants that are newly selected, create the RBAC policy
      if (selectedTenantIds?.length > 0) {
        const createPromises = []
        selectedTenantIds.forEach((tenantId) => {
          // Skip tenants that are already created
          if (existingTenantIds?.includes(tenantId)) {
            return
          }

          // Create new policy for newly selected tenants
          createPromises.push(
            neutron.createNetworkRBACPolicy({
              body: {
                rbac_policy: {
                  action: 'access_as_shared',
                  object_type: 'network',
                  target_tenant: tenantId,
                  object_id: network?.id,
                },
              },
            }),
          )
        })
        await Promise.all(createPromises)
      }

      reloadNetworks(true)
      handleClose()
    } catch (error) {
      setError({
        title: 'Error Sharing Network',
        message:
          error?.message || error?.err?.message || 'Failed to share network with selected tenants',
      })
    } finally {
      setSubmitting(false)
    }
  }, [params.selectedTenants, network, updateParams, tenants])

  const handleClose = () => {
    setSubmitting(false)
    setError(null)
    onClose()
  }

  // Disable submit if no tenants are selected or if all tenants are already selected
  const disableSubmit = useMemo(
    () =>
      (params.selectedTenants.length === 0 ||
        params.selectedTenants.every((tenant) => !!existingTenantIds.includes(tenant.id))) &&
      params.selectedTenants.length === existingTenantIds.length,
    [params.selectedTenants, existingTenantIds, tenants],
  )

  return (
    <ModalForm
      open
      title={`Share Network With Tenants`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={submitting}
      error={error}
      initialValues={params}
      submitTitle={'Save Changes'}
      disableSubmit={disableSubmit}
    >
      <Progress
        renderContentOnMount={!loadingTenants}
        loading={loadingTenants}
        message={'Loading Tenants...'}
      >
        <Text component="p" variant="caption1">
          Choose Tenants to Share Network With:
        </Text>
        <br />
        <ListTable
          searchTargets={['name']}
          columns={columns}
          data={filteredTenants}
          rowsPerPage={10}
          selectedRows={params.selectedTenants}
          onSelectedRowsChange={(selectedRows) => {
            updateParams({ selectedTenants: selectedRows })
          }}
          noScroll
        />
      </Progress>
    </ModalForm>
  )
}

export default ShareNetworkWithSpecificTenants
